import {
  BarChart3,
  BriefcaseBusiness,
  Building2,
  Boxes,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  ContactRound,
  LogOut,
  Gauge,
  GraduationCap,
  Menu,
  MapPinned,
  PackageSearch,
  Images,
  Settings,
  ShieldCheck,
  ScrollText,
  ShoppingBag,
  Store,
  Tags,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useLayoutEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { readableForeground } from '../lib/themeColors';
import { useTheme } from '../theme/ThemeContext';
import type { Capability } from '../types/domain';
import { BrandLogo } from './BrandLogo';
import { ThemeToggle } from './ThemeToggle';
import { UserAvatar } from './UserAvatar';

interface NavigationLink {
  kind: 'link';
  to: string;
  label: string;
  icon: LucideIcon;
  capability: Capability;
}

interface NavigationGroup {
  kind: 'group';
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavigationLink[];
}

type NavigationEntry = NavigationLink | NavigationGroup;

const navigation: NavigationEntry[] = [
  { kind: 'link', to: '/', label: 'Overview', icon: BarChart3, capability: 'overview:view' },
  {
    kind: 'group',
    id: 'directory',
    label: 'Directory',
    icon: ContactRound,
    items: [
      { kind: 'link', to: '/organisations', label: 'Organisations', icon: Building2, capability: 'organizations:view' },
      { kind: 'link', to: '/users', label: 'Users', icon: ContactRound, capability: 'users:view' },
      { kind: 'link', to: '/talent', label: 'Talent', icon: UsersRound, capability: 'talent:view' },
    ],
  },
  {
    kind: 'group',
    id: 'campaigns',
    label: 'Campaigns',
    icon: BriefcaseBusiness,
    items: [
      { kind: 'link', to: '/campaigns', label: 'Campaigns', icon: BriefcaseBusiness, capability: 'campaigns:view' },
      { kind: 'link', to: '/assignments', label: 'Assignments', icon: ClipboardList, capability: 'assignments:view' },
      { kind: 'link', to: '/survey-templates', label: 'Survey templates', icon: ClipboardList, capability: 'surveys:manage' },
    ],
  },
  {
    kind: 'group',
    id: 'catalogue',
    label: 'Catalogue',
    icon: PackageSearch,
    items: [
      { kind: 'link', to: '/venues', label: 'Stores', icon: Store, capability: 'venues:view' },
      { kind: 'link', to: '/activity-sites', label: 'Activity sites', icon: MapPinned, capability: 'venues:view' },
      { kind: 'link', to: '/products', label: 'Products', icon: PackageSearch, capability: 'products:view' },
      { kind: 'link', to: '/product-brands', label: 'Brands', icon: Tags, capability: 'products:view' },
      { kind: 'link', to: '/product-families', label: 'Product families', icon: Boxes, capability: 'products:view' },
      { kind: 'link', to: '/product-categories', label: 'Categories', icon: Tags, capability: 'products:view' },
    ],
  },
  {
    kind: 'group',
    id: 'operations',
    label: 'Operations',
    icon: ClipboardCheck,
    items: [
      { kind: 'link', to: '/attendance', label: 'Attendance', icon: ClipboardCheck, capability: 'attendance:view' },
      { kind: 'link', to: '/sales', label: 'Sales', icon: ShoppingBag, capability: 'sales:view' },
      { kind: 'link', to: '/shelf-evidence', label: 'Shelf evidence', icon: Images, capability: 'shelf_evidence:view' },
      { kind: 'link', to: '/stock', label: 'Stock', icon: Boxes, capability: 'stock:view' },
    ],
  },
  {
    kind: 'group',
    id: 'fieldwork',
    label: 'Fieldwork',
    icon: MapPinned,
    items: [
      { kind: 'link', to: '/activity-sessions', label: 'Activity sessions', icon: GraduationCap, capability: 'campaigns:view' },
      { kind: 'link', to: '/surveys', label: 'Field surveys', icon: ClipboardList, capability: 'surveys:view' },
      { kind: 'link', to: '/supervision', label: 'Supervision', icon: ShieldCheck, capability: 'supervision:view' },
      { kind: 'link', to: '/merchandising', label: 'Merchandising visits', icon: ClipboardList, capability: 'merchandising:view' },
    ],
  },
  { kind: 'link', to: '/performance', label: 'Performance', icon: Gauge, capability: 'performance:view' },
  {
    kind: 'group',
    id: 'administration',
    label: 'Administration',
    icon: Settings,
    items: [
      { kind: 'link', to: '/audit', label: 'Audit log', icon: ScrollText, capability: 'audit:view' },
      { kind: 'link', to: '/settings', label: 'Settings', icon: Settings, capability: 'settings:view' },
    ],
  },
];

function allowedNavigation(capabilities: Capability[]) {
  return navigation.flatMap((entry): NavigationEntry[] => {
    if (entry.kind === 'link') {
      return capabilities.includes(entry.capability) ? [entry] : [];
    }
    const items = entry.items.filter(({ capability }) => capabilities.includes(capability));
    return items.length > 0 ? [{ ...entry, items }] : [];
  });
}

function groupForPath(entries: NavigationEntry[], pathname: string) {
  return entries.find((entry) => entry.kind === 'group' && entry.items.some(
    ({ to }) => pathname === to || pathname.startsWith(`${to}/`),
  ));
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const {
    capabilities,
    profile,
    selectedWorkspace,
    selectWorkspace,
    signOut,
    workspaces,
  } = useAuth();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const isDemoEnvironment = import.meta.env.VITE_DEPLOYMENT_ENVIRONMENT === 'demo';
  const visibleNavigation = allowedNavigation(capabilities);
  const activeGroup = groupForPath(visibleNavigation, location.pathname);
  const [navigationState, setNavigationState] = useState(() => ({
    pathname: location.pathname,
    expandedGroup: activeGroup?.kind === 'group' ? activeGroup.id : null,
  }));
  const expandedGroup = navigationState.pathname === location.pathname
    ? navigationState.expandedGroup
    : activeGroup?.kind === 'group' ? activeGroup.id : null;
  const roleLabel = selectedWorkspace?.roles.length
    ? selectedWorkspace.roles.map((role) => role.replaceAll('_', ' ')).join(', ')
    : selectedWorkspace?.type === 'personal' ? 'Brand Ambassador' : 'Account';
  const useAgencyLogo = Boolean(
    selectedWorkspace?.type === 'agency'
    && selectedWorkspace.usesAgencyLogo
    && selectedWorkspace.logo,
  );
  const useAgencyName = Boolean(
    selectedWorkspace?.type === 'agency'
    && selectedWorkspace.usesAgencyName,
  );

  useLayoutEffect(() => {
    const primary = selectedWorkspace?.themePrimaryColor;
    const accent = selectedWorkspace?.themeAccentColor;
    if (!primary || !accent) return undefined;
    const rootStyle = document.documentElement.style;
    const foreground = readableForeground(primary);
    const properties = [
      '--forest',
      '--forest-2',
      '--gold',
      '--link',
      '--mint',
      '--on-primary',
      '--on-primary-muted',
      '--workspace-nav-active',
    ];
    rootStyle.setProperty('--forest', primary);
    rootStyle.setProperty('--forest-2', `color-mix(in srgb, ${primary} 82%, white)`);
    rootStyle.setProperty('--gold', accent);
    rootStyle.setProperty('--on-primary', foreground);
    rootStyle.setProperty(
      '--on-primary-muted',
      `color-mix(in srgb, ${foreground} 72%, ${primary})`,
    );
    rootStyle.setProperty(
      '--link',
      theme === 'dark'
        ? `color-mix(in srgb, ${primary} 35%, white)`
        : foreground === '#000000' ? '#17221E' : primary,
    );
    rootStyle.setProperty(
      '--mint',
      theme === 'dark'
        ? `color-mix(in srgb, ${primary} 55%, black)`
        : `color-mix(in srgb, ${primary} 12%, white)`,
    );
    rootStyle.setProperty('--workspace-nav-active', `color-mix(in srgb, ${accent} 13%, transparent)`);
    return () => {
      properties.forEach((property) => rootStyle.removeProperty(property));
    };
  }, [selectedWorkspace?.themeAccentColor, selectedWorkspace?.themePrimaryColor, theme]);

  return (
    <div className="app-shell">
      <aside className={open ? 'sidebar open' : 'sidebar'}>
        <div className="sidebar-brand">
          {useAgencyLogo || useAgencyName ? (
            <>
              {useAgencyLogo ? (
                <img
                  alt=""
                  className="agency-brand-logo-sidebar"
                  src={selectedWorkspace?.logo ?? undefined}
                />
              ) : null}
              {useAgencyName ? <span>{selectedWorkspace?.name}</span> : null}
            </>
          ) : (
            <>
              <BrandLogo className="brand-logo-sidebar" decorative />
              <span>Brand Field Crew</span>
            </>
          )}
          <button aria-label="Close menu" className="icon-button mobile-only" onClick={() => setOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav aria-label="Main navigation">
          {visibleNavigation.map((entry) => {
            const Icon = entry.icon;
            if (entry.kind === 'link') {
              return (
                <NavLink
                  className="sidebar-nav-link"
                  end={entry.to === '/'}
                  key={entry.to}
                  onClick={() => {
                    setNavigationState({ pathname: entry.to, expandedGroup: null });
                    setOpen(false);
                  }}
                  to={entry.to}
                >
                  <Icon size={19} /> {entry.label}
                </NavLink>
              );
            }

            const expanded = expandedGroup === entry.id;
            const active = activeGroup?.kind === 'group' && activeGroup.id === entry.id;
            return (
              <div className="sidebar-nav-group" key={entry.id}>
                <button
                  aria-controls={`sidebar-group-${entry.id}`}
                  aria-expanded={expanded}
                  className={active ? 'sidebar-nav-group-toggle active' : 'sidebar-nav-group-toggle'}
                  onClick={() => setNavigationState({
                    pathname: location.pathname,
                    expandedGroup: expanded ? null : entry.id,
                  })}
                  type="button"
                >
                  <Icon size={19} />
                  <span>{entry.label}</span>
                  <ChevronDown className="sidebar-nav-chevron" size={16} />
                </button>
                {expanded ? (
                  <div className="sidebar-nav-group-items" id={`sidebar-group-${entry.id}`}>
                    {entry.items.map(({ to, label, icon: ItemIcon }) => (
                      <NavLink
                        className="sidebar-nav-link sidebar-nav-child"
                        key={to}
                        onClick={() => {
                          setNavigationState({ pathname: to, expandedGroup: entry.id });
                          setOpen(false);
                        }}
                        to={to}
                      >
                        <ItemIcon size={17} /> {label}
                      </NavLink>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-account">
          <NavLink className="sidebar-profile" onClick={() => setOpen(false)} to="/profile">
            <UserAvatar className="avatar" name={profile?.fullName ?? 'User'} src={profile?.avatar} />
            <span>
              <strong>{profile?.fullName ?? 'Account'}</strong>
              <small>{roleLabel}</small>
            </span>
            <ChevronRight size={17} />
          </NavLink>
          <button className="sidebar-signout" onClick={signOut} type="button"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>

      {open ? <button aria-label="Close menu overlay" className="sidebar-overlay" onClick={() => setOpen(false)} /> : null}

      <div className="app-main">
        <header className="topbar">
          <button aria-label="Open menu" className="icon-button mobile-only" onClick={() => setOpen(true)}>
            <Menu size={22} />
          </button>
          {workspaces.length > 1 ? (
            <label className="workspace-picker">
              <select aria-label="Active workspace" onChange={(event) => selectWorkspace(event.target.value)} value={selectedWorkspace?.id ?? ''}>
                {workspaces.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}
              </select>
              <ChevronDown size={16} />
            </label>
          ) : (
            <span className="workspace-picker workspace-name">{selectedWorkspace?.name ?? 'Workspace'}</span>
          )}
          {selectedWorkspace?.logo ? (
            <div className="workspace-logo">
              <img
                alt={`${selectedWorkspace.name} logo`}
                key={selectedWorkspace.logo}
                onError={(event) => { event.currentTarget.hidden = true; }}
                src={selectedWorkspace.logo}
              />
            </div>
          ) : null}
          {isDemoEnvironment ? <span className="environment-badge">Demo</span> : null}
          <ThemeToggle />
        </header>
        <main className="page-container">{children}</main>
      </div>
    </div>
  );
}
